import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import {
  LucideAngularModule,
  Sun,
  Moon,
  Check,
  X,
  TriangleAlert,
  Info,
  type LucideIconData,
  CircleAlert,
  CircleAlertIcon,
  ArrowLeft,
  Mail,
  ArrowRight,
  Minus,
  Lock,
  Eye,
  EyeOff,
  SquarePen,
  Trash,
  Pencil,
  User,
  Shield,
  ChartNoAxesCombined,
  LayoutDashboard,
  Bell,
  Search,
  Settings,
  LogOut,
  ChevronsRight,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  ClipboardCheck,
  CirclePlus,
  ChartLine,
  BetweenHorizontalStart,
  Users,
  Headset,
  Target,
  FileText,
  Building2,
  TrendingUp,
  ChartColumn,
  Sparkles,
  ArrowUp,
  ArrowDown,
  FileText as FileTextIcon,
  Globe,
  Smartphone,
  Monitor,
  Clock,
  SquareTerminal,
  EllipsisVertical,
  BookOpenCheck,
  Copy,
  GripVertical,
  TrendingDown,
  List,
  ServerCrash,
  ChartBar,
  MessageSquareMore,
  Bot,
  Send,
  SquareMenu,
  CircleDollarSignIcon,
  CircleCheckBig,
  CircleX,
  CreditCard,
  Landmark,
  Wallet,
  DollarSignIcon,
  RefreshCcw,
  MousePointer,
  Heart,
  GitCommitHorizontal,
  PhoneOff,
  Play,
  Pause,
  Undo2,
  Youtube,
  Star,
  ArrowUpDown,
  PhoneCall,
  UserCheck,
  MousePointerClick,
  Plus,
  RotateCcw,
  Loader2,
  Inbox,
  Hourglass,
  Activity,
  Zap,
  WalletCards,
  Gift,
  ShoppingCart,
  FileCheck,
  Gauge,
  ChartPie,
  RadioIcon,
  CloudSun,
  CalendarClock,
  ShoppingBag,
  Download,
  Folder,
  Upload,
  Filter,
  Package,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Volume2,
  ScreenShare,
  Hand,
  Pin,
  PinOff,
  SwitchCamera,
  LayoutGrid,
  PictureInPicture2,
  Archive,
  Signal,
  SignalHigh,
  SignalMedium,
  SignalLow,
  SignalZero,
  Brush
} from 'lucide-angular';

/**
 * Background / visual effects — Lucide's `square-user` with its top-right
 * corner opened up for a sparkle. Every meeting app uses this glyph for camera
 * effects and Lucide has no single icon for it, so it is composed here from the
 * two it does have, on the same 24×24 grid and 2px stroke as the rest of the
 * set (they sit side by side in the call toolbar). The sparkle is FILLED: at a
 * 15px render an outlined star that small closes into a blob.
 */
export const VIDEO_EFFECTS_ICON: LucideIconData = [
  ['path', { d: 'M13.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9.5' }],
  ['circle', { cx: '12', cy: '10', r: '3' }],
  ['path', { d: 'M7 21v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2' }],
  ['path', {
    d: 'M19 1.2q0.7 3.1 3.8 3.8-3.1 0.7-3.8 3.8-0.7-3.1-3.8-3.8 3.1-0.7 3.8-3.8Z',
    fill: 'currentColor', stroke: 'none',
  }],
];

export const ICON_MAP = {
  upload: Upload,
  arrowUpDown: ArrowUpDown,
  sun: Sun,
  moon: Moon,
  check: Check,
  circleAlert: CircleAlert,
  circleAlertIcon: CircleAlertIcon,
  x: X,
  triangleAlert: TriangleAlert,
  info: Info,
  arrowLeft: ArrowLeft,
  mail: Mail,
  arrowRight: ArrowRight,
  minus: Minus,
  lock: Lock,
  eye: Eye,
  eyeOff: EyeOff,
  squarePen: SquarePen,
  trash: Trash,
  pencil: Pencil,
  user: User,
  shield: Shield,
  chartNoAxesCombined: ChartNoAxesCombined,
  layoutDashboard: LayoutDashboard,
  squareTerminal: SquareTerminal,
  bell: Bell,
  search: Search,
  settings: Settings,
  logOut: LogOut,
  headset: Headset,
  chevronsRight: ChevronsRight,
  chevronsLeft: ChevronsLeft,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronUp: ChevronUp,
  chevronDown: ChevronDown,
  calendar: Calendar,
  ChevronsLeft: ChevronsLeft,
  ChevronRight: ChevronRight,
  ChevronsRight: ChevronsRight,
  ChevronLeft: ChevronLeft,
  clipboard: ClipboardCheck,
  circlePlus: CirclePlus,
  chartLine: ChartLine,
  betweenHorizontalStart: BetweenHorizontalStart,
  users: Users,
  target: Target,
  fileText: FileText,
  building2: Building2,
  trendingUp: TrendingUp,
  trendingDown: TrendingDown,
  chartColumn: ChartColumn,
  sparkles: Sparkles,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  globe: Globe,
  smartphone: Smartphone,
  monitor: Monitor,
  file: FileTextIcon,
  clock: Clock,
  serverCrash: ServerCrash,
  ellipsisVertical: EllipsisVertical,
  bookOpenCheck: BookOpenCheck,
  copy: Copy,
  gripVertical: GripVertical,
  list: List,
  chartBar: ChartBar,
  messageSquareMore: MessageSquareMore,
  bot: Bot,
  send: Send,
  squareMenu: SquareMenu,
  circleDollarSign: CircleDollarSignIcon,
  circleCheckBig: CircleCheckBig,
  circleX: CircleX,
  creditCard: CreditCard,
  landmark: Landmark,
  wallet: Wallet,
  dollarSign: DollarSignIcon,
  refreshCcw: RefreshCcw,
  mousePointer: MousePointer,
  heart: Heart,
  gitCommitHorizontal: GitCommitHorizontal,
  phoneOff: PhoneOff,
  play: Play,
  pause: Pause,
  undo2: Undo2,
  star: Star,
  phoneCall: PhoneCall,
  userCheck: UserCheck,
  mousePointerClick: MousePointerClick,
  plus: Plus,
  rotateCcw: RotateCcw,
  loader: Loader2,
  inbox: Inbox,
  hourglass: Hourglass,
  activity: Activity,
  zap: Zap,
  walletCards: WalletCards,
  gift: Gift,
  shoppingCart: ShoppingCart,
  fileCheck: FileCheck,
  gauge: Gauge,
  chartPie: ChartPie,
  radioIcon: RadioIcon,
  cloudSun: CloudSun,
  calendarClock: CalendarClock,
  shoppingBag: ShoppingBag,
  download: Download,
  folder: Folder,
  filter: Filter,
  box: Package,
  video: Video,
  videoOff: VideoOff,
  mic: Mic,
  micOff: MicOff,
  volume2: Volume2,
  screenShare: ScreenShare,
  hand: Hand,
  pin: Pin,
  pinOff: PinOff,
  switchCamera: SwitchCamera,
  layoutGrid: LayoutGrid,
  pictureInPicture: PictureInPicture2,
  archive: Archive,
  signal: Signal,
  signalHigh: SignalHigh,
  signalMedium: SignalMedium,
  signalLow: SignalLow,
  signalZero: SignalZero,
  brush: Brush,
  videoEffects: VIDEO_EFFECTS_ICON
} as const;

export type IconName = keyof typeof ICON_MAP;

/**
 * @example
 * <app-icon name="user" [size]="24" class="text-blue-500" />
 * <app-icon name="settings" [size]="20" />
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `<lucide-icon
    [img]="iconData"
    [size]="size"
    [class]="class"
  ></lucide-icon>`,
})
export class IconComponent {
  @Input({ required: true }) name!: IconName;
  @Input() size: number = 24;
  @Input() class: string = '';

  get iconData(): LucideIconData {
    return ICON_MAP[this.name];
  }
}
